import _replace from 'lodash/replace';
import _includes from 'lodash/includes';
import bigDecimal from 'js-big-decimal';

const persianNumber = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const englishNumber = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const charachter = ['.', ','];

function suffix(
  num: number | string,
  precision: number = 3,
  round: 'roundDown' | 'roundUp' = 'roundDown'
): string {
  const map = [
    { suffix: 'T', threshold: 1e12 },
    { suffix: 'B', threshold: 1e9 },
    { suffix: '', threshold: 1 }
  ];

  const found = map.find(x => Math.abs(Number(num)) >= x.threshold);
  const func = {
    roundDown,
    roundUp
  };
  if (found) {
    const formatted =
      func[round](Number(num) / found.threshold, precision) + found.suffix;
    return formatted;
  }

  return num.toString();
}

const divide = (
  num1: number | string,
  num2: number | string,
  precision: any
): string => {
  if (Number(num1) === 0)
    console.log('can not divide by zero! - (arguments[0])');
  if (Number(num2) === 0)
    console.log('can not divide by zero! - (arguments[1])');
  const number1 = Number(num1) || 1;
  const number2 = Number(num2) || 1;
  return bigDecimal.divide(number1, number2, precision);
};

const toEn = (num: string) => {
  const find = [...persianNumber, '٫'];
  const replace = [...englishNumber, '.'];
  let replaceString = num;
  let regex;
  for (let i = 0; i < find.length; i++) {
    regex = new RegExp(find[i], 'g');
    replaceString = replaceString.replace(regex, replace[i]);
  }
  return replaceString;
};

const isValid = (num: number | string) => {
  const numbers = typeof num === 'number' ? num.toString() : num;
  if (numbers.length === 0) return true;
  const allowCharachter = [...persianNumber, ...englishNumber, ...charachter];
  if (_includes(allowCharachter, numbers.slice(-1))) {
    return true;
  }
  return false;
};

const delimiter = (
  num: number | string,
  separator: string = ',',
  separateBy: number = 3
) => {
  const numbers = typeof num === 'number' ? num.toString() : num;
  if (numbers) {
    const parts = numbers.split('.');
    const regex = new RegExp(`(\\d)(?=(\\d{${separateBy}})+(?!\\d))`, 'g');
    parts[0] = _replace(parts[0].split(',').join(''), regex, `$1${separator}`);
    return parts.join('.');
  }
  return num;
};

// New function to reverse the delimiter and remove separator
const removeDelimiter = (num: string, separator: string = ','): number => {
  const cleanedNumber = num.split(separator).join('');
  return parseFloat(cleanedNumber);
};

const roundUp = (val: number, dec: number): string => {
  const decimal = dec === 0 ? 0 : +`1${new Array(dec + 1).join('0')}`;
  const result =
    decimal === 0
      ? Math.ceil(val)
      : Math.ceil(+bigDecimal.multiply(val, decimal)) / decimal;
  return result
    ? result
        .toFixed(dec)
        .replace(/^([\d,]+)$|^([\d,]+)\.0*$|^([\d,]+\.[0-9]*?)0*$/, '$1$2$3')
    : '0';
};

const roundDown = (val: number, dec: number): string => {
  const decimal = dec === 0 ? 0 : +`1${new Array(dec + 1).join('0')}`;
  const result =
    decimal === 0
      ? Math.floor(val)
      : Math.floor(+bigDecimal.multiply(val, decimal)) / decimal;
  return result
    ? result
        .toFixed(dec)
        .replace(/^([\d,]+)$|^([\d,]+)\.0*$|^([\d,]+\.[0-9]*?)0*$/, '$1$2$3')
    : '0';
};

const isDecimalValid = (val: string, dec: number): boolean => {
  const stringVal = val.toString();
  return stringVal.indexOf('.') >= 0
    ? !(stringVal.split('.')[1].length > dec)
    : true;
};

const formatNumber = (
  num: number | string,
  precision: number = 3,
  separator: string = ','
): string => {
  const numberStr = typeof num === 'number' ? num.toString() : String(num);
  let [integerPart, decimalPart] = numberStr.split('.');
  integerPart = String(delimiter(integerPart || '', separator));
  if (decimalPart) {
    decimalPart = decimalPart.slice(0, precision);
    decimalPart = decimalPart.replace(/0+$/, '');
    if (decimalPart.length > 0) {
      return `${integerPart}.${decimalPart}`;
    }
  }
  return integerPart;
};

export default {
  delimiter: (num: number | string, separator?: string, separateBy?: number) =>
    delimiter(num, separator, separateBy),
  roundDown: (val: number, dec: number) => roundDown(val, dec),
  roundUp: (val: number, dec: number) => roundUp(val, dec),
  isDecimalValid: (val: string, dec: number): boolean =>
    isDecimalValid(val, dec),
  isValid: (val: number | string): boolean => isValid(val),
  toEn: (val: string) => toEn(val),
  formatNumber: (
    num: number | string,
    precision: number = 3,
    separator: string = ','
  ) => formatNumber(num, precision, separator),
  removeDelimiter: (num: string, separator: string = ',') =>
    removeDelimiter(num, separator), // New function added
  suffix,
  bigDecimal,
  modulus: bigDecimal.modulus,
  divide,
  multiply: bigDecimal.multiply,
  subtract: bigDecimal.subtract,
  add: bigDecimal.add,
  negate: bigDecimal.negate,
  compareTo: bigDecimal.compareTo,
  ceil: bigDecimal.ceil,
  floor: bigDecimal.floor,
  abs: bigDecimal.abs,
  round: bigDecimal.round,
  getPrettyValue: bigDecimal.getPrettyValue
};
